import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import PropTypes from "prop-types"
import { fetchPortalTenantResolve } from "services/portalTenantApi"
import {
  applyTenantBranding,
  normalizeTenantBranding,
} from "utils/tenantBranding"
import {
  getCurrentPortalHostname,
  shouldResolvePortalTenant,
} from "utils/portalHost"
import {
  readPortalTenantCache,
  writePortalTenantCache,
} from "utils/portalTenantCache"

const idleState = {
  loading: false,
  resolved: false,
  useCustomLogin: false,
  isCustomLoginActive: false,
  tenantId: null,
  tenantName: "",
  tenantBranding: null,
}

const buildPortalTenantState = cachedData => {
  const useCustomLogin = Boolean(cachedData?.useCustomLogin)
  const tenantBranding = cachedData?.tenantBranding || null

  if (useCustomLogin && tenantBranding) {
    applyTenantBranding(tenantBranding)
  }

  return {
    loading: false,
    resolved: Boolean(cachedData),
    useCustomLogin,
    isCustomLoginActive: useCustomLogin,
    tenantId: cachedData?.tenantId || null,
    tenantName: cachedData?.name || "",
    tenantBranding,
  }
}

const getInitialPortalTenantState = () => {
  if (!shouldResolvePortalTenant()) {
    return idleState
  }

  const host = getCurrentPortalHostname()
  const cached = readPortalTenantCache(host)
  if (cached) {
    return buildPortalTenantState(cached)
  }

  return {
    ...idleState,
    loading: true,
  }
}

const PortalTenantContext = createContext(idleState)

export const PortalTenantProvider = ({ children }) => {
  const [state, setState] = useState(getInitialPortalTenantState)

  useEffect(() => {
    let cancelled = false

    if (!shouldResolvePortalTenant()) {
      setState(idleState)
      return undefined
    }

    const host = getCurrentPortalHostname()

    fetchPortalTenantResolve(host)
      .then(data => {
        if (cancelled) return

        const useCustomLogin = Boolean(data?.useCustomLogin)
        const tenantBranding = data?.tenantBranding
          ? normalizeTenantBranding(data.tenantBranding)
          : null

        writePortalTenantCache(host, {
          tenantId: data?.tenantId,
          name: data?.name,
          useCustomLogin,
          tenantBranding,
        })

        if (useCustomLogin && tenantBranding) {
          applyTenantBranding(tenantBranding)
        }

        setState({
          loading: false,
          resolved: true,
          useCustomLogin,
          isCustomLoginActive: useCustomLogin,
          tenantId: data?.tenantId || null,
          tenantName: data?.name || "",
          tenantBranding,
        })
      })
      .catch(() => {
        if (!cancelled) {
          setState(prev => ({
            ...prev,
            loading: false,
            resolved: false,
            useCustomLogin: false,
            isCustomLoginActive: false,
          }))
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo(() => state, [state])

  return (
    <PortalTenantContext.Provider value={value}>
      {children}
    </PortalTenantContext.Provider>
  )
}

PortalTenantProvider.propTypes = {
  children: PropTypes.node,
}

export const usePortalTenant = () => useContext(PortalTenantContext)

export default PortalTenantContext
